import {useEffect} from 'react'
import { HashRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from 'react-redux'
import { LinkContainer } from 'react-router-bootstrap'
import { Container } from 'react-bootstrap'
import Header from './components/Header'
import Footer from './components/Footer'

import HomeScreen from './screens/HomeScreen'
import LoginScreen from './screens/LoginScreen'
import RegisterScreen from './screens/RegisterScreen'
import ProfileScreen from './screens/ProfileScreen'
import UserListScreen from './screens/UserListScreen'
import UserEditScreen from './screens/UserEditScreen'
import AdminDashboardScreen from './screens/AdminDashboardScreen'
import PaymentTypeListScreen from './screens/PaymentTypeListScreen'
import PaymentTypeEditScreen from './screens/PaymentTypeEditScreen'
import AwardListScreen from './screens/AwardListScreen'
import AwardEditScreen from './screens/AwardEditScreen'
import ContestListScreen from './screens/ContestListScreen'
import ContestEditScreen from './screens/ContestEditScreen'
import CourseListScreen from './screens/CourseListScreen'
import CourseEditScreen from './screens/CourseEditScreen'
import TournamentListScreen from './screens/TournamentListScreen'
import TournamentEditScreen from './screens/TournamentEditScreen'
import BlogListScreen from './screens/BlogListScreen'
import BlogEditScreen from './screens/BlogEditScreen'
import LeagueScreen from './screens/LeagueScreen'
import LeagueAdminScreen from './screens/LeagueAdminScreen'
import LeagueInfoScreen from './screens/LeagueInfoScreen'
import LeaderboardScreen from './screens/LeaderboardScreen'
import EventsScreen from './screens/EventsScreen'
import TournamentDetailScreen from './screens/TournamentDetailScreen'
import PlayersScreen from './screens/PlayersScreen'

function App() {
  const tournamentList = useSelector(state => state.tournamentList)
  const { tournaments } = tournamentList
  
  // Check if user is logged in
  const userLogin = useSelector(state => state.userLogin)
  const { userInfo } = userLogin
  const isLoggedIn = !!userInfo

  // Find active tournament
  const sortedTournaments = tournaments
    ? [...tournaments].sort((a, b) => {
        if (a.start_date && b.start_date) return new Date(b.start_date) - new Date(a.start_date)
        return b.id - a.id
      })
    : []
  const liveTournament = sortedTournaments.find(t => t.status === 'active')

  return (
    <Router >
      <Header />
      
      {/* ── LIVE event banner (full-width) ────────────────────── */}
      {liveTournament && (
        <>
          <style>{`@keyframes livePulse { 0%,100%{opacity:1} 50%{opacity:0.55} }`}</style>
          <div style={{
            background: '#dc2626',
            padding: '0.6rem 1.5rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            boxSizing: 'border-box',
            cursor: 'pointer',
          }} onClick={() => window.location.hash = `#/events/${liveTournament.id}`}>
            <span style={{
              background: 'rgba(255,255,255,0.2)', color: '#fff',
              fontWeight: 900, fontSize: '0.62rem', letterSpacing: '0.12em',
              textTransform: 'uppercase', padding: '0.15rem 0.5rem', borderRadius: 4,
              animation: 'livePulse 1.5s ease-in-out infinite',
            }}>● LIVE</span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>
              {liveTournament.name} is happening now
            </span>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              View Event <i className="fa-solid fa-arrow-right" style={{ fontSize: '0.7rem' }}></i>
            </span>
          </div>
        </>
      )}
      
      <main className='py-3'>
        <Container fluid className='px-4' style={{ maxWidth: '1400px', marginLeft: 'auto', marginRight: 'auto' }}>
          <Routes>
            {/* Public routes - always accessible */}
            <Route path="/login" element={<LoginScreen/>} />
            <Route path="/register" element={<RegisterScreen/>} />
            
            {/* Protected routes - only accessible if logged in */}
            {isLoggedIn ? (
              <>
                <Route path="/" element={<HomeScreen />} exact />
                <Route path="/profile" element={<ProfileScreen/>} />
                <Route path="/events" element={<EventsScreen />} />
                <Route path="/events/:id" element={<TournamentDetailScreen />} />
                <Route path="/leaderboard" element={<LeaderboardScreen />} />
                <Route path="/players" element={<PlayersScreen />} />
                <Route path="/stats" element={<HomeScreen />} />
                <Route path="/admin/dashboard" element={<AdminDashboardScreen/>} />
                <Route path="/admin/userList" element={<UserListScreen/>} />
                <Route path="/admin/payment-types" element={<PaymentTypeListScreen/>} />
                <Route path="/admin/payment-types/create" element={<PaymentTypeEditScreen/>} />
                <Route path="/admin/payment-types/:id/edit" element={<PaymentTypeEditScreen/>} />
                <Route path="/admin/awards" element={<AwardListScreen/>} />
                <Route path="/admin/awards/create" element={<AwardEditScreen/>} />
                <Route path="/admin/awards/:id/edit" element={<AwardEditScreen/>} />
                <Route path="/admin/contests" element={<ContestListScreen/>} />
                <Route path="/admin/contests/create" element={<ContestEditScreen/>} />
                <Route path="/admin/contests/:id/edit" element={<ContestEditScreen/>} />
                <Route path="/admin/courses" element={<CourseListScreen/>} />
                <Route path="/admin/courses/create" element={<CourseEditScreen/>} />
                <Route path="/admin/courses/:id/edit" element={<CourseEditScreen/>} />
                <Route path="/admin/tournaments" element={<TournamentListScreen/>} />
                <Route path="/admin/tournaments/create" element={<TournamentEditScreen/>} />
                <Route path="/admin/tournaments/:id/edit" element={<TournamentEditScreen/>} />
                <Route path="/admin/user/:id/edit" element={<UserEditScreen/>} />
                <Route path="/news" element={<BlogListScreen/>} />
                <Route path="/news/create" element={<BlogEditScreen/>} />
                <Route path="/news/:id/edit" element={<BlogEditScreen/>} />
                <Route path="/league" element={<LeagueInfoScreen/>} />
                <Route path="/league/dashboard" element={<LeagueScreen/>} />
                <Route path="/admin/leagues" element={<LeagueAdminScreen/>} />
                <Route path="/admin/leagues/:id" element={<LeagueAdminScreen/>} />
                {/* Catch-all for logged-in users - go to home if invalid route */}
                <Route path="*" element={<Navigate to="/" />} />
              </>
            ) : (
              /* Not logged in - redirect everything except login/register to login */
              <Route path="*" element={<Navigate to="/login" />} />
            )}
          </Routes>
        </Container>
      </main>
      <Footer />
    </Router>
  );
}

export default App;
